import type {
  IChartData,
  IChartDataOrbit,
  IChartDataSignal,
  IItem,
} from "../../scheme/components/analytics/interfaces";

const VALUE2_UNIT_CODE = 133;
const SERIES_LABEL_SEPARATOR = " / ";

export interface IMultiAnalysisTrendMeasureValue {
  date: string;
  value: number;
  units: string;
  unitCode: number | null;
}

export interface IMultiAnalysisTrendMeasureListItem {
  key: string;
  color: string;
  pointName: string;
  measureName: string;
  value1: IMultiAnalysisTrendMeasureValue | null;
  value2: IMultiAnalysisTrendMeasureValue | null;
}

type TGraphicAreaCellData =
  | {
      kind: "signal";
      data: IChartDataSignal[];
    }
  | {
      kind: "orbit" | "polar";
      data: IChartDataOrbit[];
    };

const toTimestamp = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value.replace("Z", "")).getTime();

  return Number.isFinite(timestamp) ? timestamp : null;
};

const normalizeKeyPart = (value: unknown): string => String(value ?? "").trim();

export const buildMultiAnalysisMeasureKey = (
  idPoint: number | null | undefined,
  measureName: string | null | undefined,
  fallbackName?: string | null,
  idMeasure?: number | null,
): string | null => {
  const normalizedMeasureName = normalizeKeyPart(measureName) || normalizeKeyPart(fallbackName);

  if (!Number.isFinite(Number(idPoint)) || !normalizedMeasureName) {
    return null;
  }

  if (idMeasure != null && Number.isFinite(Number(idMeasure))) {
    return `${Number(idPoint)}:${Number(idMeasure)}:${normalizedMeasureName}`;
  }

  return `${Number(idPoint)}:${normalizedMeasureName}`;
};

export const getTrendSeriesMeasureKey = (
  series: Pick<IChartData, "idMeasure" | "idPoint" | "parameterName" | "name">,
) => buildMultiAnalysisMeasureKey(series.idPoint, series.parameterName, series.name, series.idMeasure);

const getSignalSeriesMeasureKey = (
  series: Pick<IChartDataSignal, "idMeasure" | "idPoint" | "measurementName" | "name" | "pointName">,
) =>
  buildMultiAnalysisMeasureKey(
    series.idPoint,
    series.measurementName,
    series.name || series.pointName,
    series.idMeasure,
  );

const getOrbitSeriesMeasureKey = (series: Pick<IChartDataOrbit, "idPoint" | "parameterName" | "name">) =>
  buildMultiAnalysisMeasureKey(series.idPoint, series.parameterName, series.name);

const splitSeriesLabel = (
  series: Pick<IChartData, "name" | "parameterName">,
): { pointName: string; measureName: string } => {
  const separatorIndex = series.name.indexOf(SERIES_LABEL_SEPARATOR);

  if (separatorIndex === -1) {
    return {
      pointName: series.name,
      measureName: series.parameterName ?? series.name,
    };
  }

  return {
    pointName: series.name.slice(0, separatorIndex),
    measureName:
      series.name.slice(separatorIndex + SERIES_LABEL_SEPARATOR.length) || series.parameterName || series.name,
  };
};

const findLatestItem = (items: readonly IItem[]): IItem | null =>
  items.reduce<IItem | null>((latest, item) => {
    const itemTimestamp = toTimestamp(item.date);

    if (itemTimestamp === null) {
      return latest;
    }

    const latestTimestamp = toTimestamp(latest?.date);

    return latestTimestamp === null || itemTimestamp > latestTimestamp ? item : latest;
  }, null);

const findNearestItemByDate = (items: readonly IItem[], cursorDate: string | null | undefined): IItem | null => {
  const cursorTimestamp = toTimestamp(cursorDate);

  if (cursorTimestamp === null) {
    return findLatestItem(items);
  }

  return items.reduce<IItem | null>((nearest, item) => {
    const itemTimestamp = toTimestamp(item.date);

    if (itemTimestamp === null) {
      return nearest;
    }

    const nearestTimestamp = toTimestamp(nearest?.date);

    return nearestTimestamp === null ||
      Math.abs(itemTimestamp - cursorTimestamp) < Math.abs(nearestTimestamp - cursorTimestamp)
      ? item
      : nearest;
  }, null);
};

const isValue2Series = (series: Pick<IChartData, "isAmplitudePhase" | "unitCode" | "units">): boolean =>
  series.isAmplitudePhase === true && Number(series.unitCode ?? series.units) === VALUE2_UNIT_CODE;

const toMeasureValue = (
  series: Pick<IChartData, "unitCode" | "units">,
  item: IItem,
): IMultiAnalysisTrendMeasureValue => ({
  date: item.date,
  value: item.value,
  units: series.units,
  unitCode: Number.isFinite(Number(series.unitCode)) ? Number(series.unitCode) : null,
});

export const buildTrendMeasureListItems = (
  series: readonly IChartData[],
  cursorDate: string | null = null,
): IMultiAnalysisTrendMeasureListItem[] => {
  const itemsByKey = new Map<string, IMultiAnalysisTrendMeasureListItem>();

  series.forEach(item => {
    const key = getTrendSeriesMeasureKey(item);
    const measureItem = findNearestItemByDate(item.items, cursorDate);

    if (!key || !measureItem) {
      return;
    }

    const label = splitSeriesLabel(item);
    const existing = itemsByKey.get(key);
    const listItem =
      existing ??
      ({
        key,
        color: item.color,
        pointName: label.pointName,
        measureName: label.measureName,
        value1: null,
        value2: null,
      } satisfies IMultiAnalysisTrendMeasureListItem);
    const value = toMeasureValue(item, measureItem);

    if (isValue2Series(item)) {
      listItem.value2 = value;
    } else {
      listItem.color = item.color;
      listItem.value1 = value;
    }

    itemsByKey.set(key, listItem);
  });

  return Array.from(itemsByKey.values());
};

export const buildTrendMeasureListFingerprint = (series: readonly IChartData[]): string =>
  JSON.stringify(
    buildTrendMeasureListItems(series).map(({ key, pointName, measureName }) => ({ key, pointName, measureName })),
  );

export const filterTrendSeriesByHiddenMeasureKeys = (
  series: readonly IChartData[],
  hiddenMeasureKeys: ReadonlySet<string>,
): IChartData[] => {
  if (hiddenMeasureKeys.size === 0) {
    return [...series];
  }

  return series.filter(item => {
    const key = getTrendSeriesMeasureKey(item);

    return !key || !hiddenMeasureKeys.has(key);
  });
};

export const filterGraphicAreaDataByHiddenMeasureKeys = (
  dataByCellId: Record<string, TGraphicAreaCellData | null>,
  hiddenMeasureKeys: ReadonlySet<string>,
): Record<string, TGraphicAreaCellData | null> => {
  if (hiddenMeasureKeys.size === 0) {
    return dataByCellId;
  }

  return Object.entries(dataByCellId).reduce<Record<string, TGraphicAreaCellData | null>>((acc, [cellId, cellData]) => {
    const setCellData = (nextCellData: TGraphicAreaCellData | null) => {
      acc[cellId] = nextCellData;
      return acc;
    };

    if (!cellData) {
      return setCellData(cellData);
    }

    if (cellData.kind === "signal") {
      const data = cellData.data.filter(series => {
        const key = getSignalSeriesMeasureKey(series);

        return !key || !hiddenMeasureKeys.has(key);
      });

      return setCellData(data.length > 0 ? { ...cellData, data } : null);
    }

    const data = cellData.data.filter(series => {
      const key = getOrbitSeriesMeasureKey(series);

      return !key || !hiddenMeasureKeys.has(key);
    });

    return setCellData(data.length > 0 ? { ...cellData, data } : null);
  }, {});
};
